import * as React from 'react';
import { connect } from 'react-redux';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import BrandProfileEditForm from './brand-profile-edit-form/BrandProfileEditForm';
import ProfileSummary from 'modules/misc/components/profile-summary/ProfileSummary';

import { closeNotification, danger } from 'redux-store/reducers/common-reducer';
import { uploadImage } from '../reducers/brandProfile';

import { isAdmin } from 'utils/account';
import { addProtocol } from 'utils/url';
import { getMissingImagesValidationError } from 'utils/profile';

import type {
  Account,
  BrandProfile,
  BrandProfileForm,
  Dispatch,
  Organization,
  Role
} from 'types';

type StateProps = {
  brandProfile: BrandProfile,
  currentAccount: Account, // eslint-disable-line react/no-unused-prop-types,
  roles: Array<Role>, // eslint-disable-line react/no-unused-prop-types,
  currentOrganization: Organization // eslint-disable-line react/no-unused-prop-types
};

type DefaultProps = {
  coverImageType?: string | null,
  mode: 'create' | 'edit'
};

type OwnProps = DefaultProps & {
  children?: React.ReactNode | null,
  onAfterSave: (a: BrandProfile) => void,
  onSave: (a: BrandProfileForm) => Promise<BrandProfile>,
  organizationType?: string
};

type DispatchProps = {
  dispatch: Dispatch,
  dispatchDanger: (message: string) => Promise<any>,
  dispatchUploadImage: (profileDetails: BrandProfileForm, type: string, image: File) => Promise<any>
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  profileDetails: BrandProfileForm,
  isModified: boolean
};

class BrandProfileEditFormContainer extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    mode: 'edit',
    coverImageType: 'cover-medium'
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      profileDetails: this.getProfileDetailsState(props),
      isModified: false
    };
  }

  getProfileDetailsState(nextProps: Props): BrandProfileForm {
    const { currentAccount, currentOrganization, brandProfile, roles } = nextProps;
    const isAdminMode = isAdmin(currentAccount, roles);

    const emptyDetails: BrandProfileForm = {
      name: isAdminMode ? '' : this.prefillFromOrganization(currentOrganization, 'name'),
      url: isAdminMode ? '' : this.prefillFromOrganization(currentOrganization, 'website'),
      pitch: '',
      contactEmail: '',
      instagramUrl: '',
      youtubeUrl: '',
      coverImage: null,
      profileImage: null,
      category: undefined
    };

    const profileDetails = {};

    Object.keys(emptyDetails).forEach((field) => {
      profileDetails[field] = (brandProfile[field] as typeof field) || emptyDetails[field];
    });

    if (brandProfile.id) {
      return profileDetails as BrandProfileForm;
    }

    return emptyDetails;
  }

  prefillFromOrganization(currentOrganization: Organization, field: string) {
    if (!currentOrganization) {
      return '';
    }

    return currentOrganization[field] || '';
  }

  addProtocolToUrls(profileDetails: BrandProfileForm) {
    const keys = ['url', 'instagramUrl', 'youtubeUrl'];

    keys.forEach(key => {
      profileDetails[key] = addProtocol(profileDetails[key]); // eslint-disable-line no-param-reassign
    });

    return profileDetails;
  }

  sendImage = (type: string, image: File) => {
    const { dispatchUploadImage } = this.props;
    const { profileDetails } = this.state;

    return dispatchUploadImage(profileDetails, type, image)
      .then(data => this.setState(state => ({
        profileDetails: {
          ...state.profileDetails,
          [`${type}Image`]: data
        },
        isModified: true
      })));
  };

  handleChange = (name: string, value: string) => {
    this.setState({
      profileDetails: {
        ...this.state.profileDetails, // eslint-disable-line react/no-access-state-in-setstate
        [name]: value
      },
      isModified: true
    });
  };

  isInvalidImages = () => {
    const { profileDetails } = this.state;
    const { dispatchDanger } = this.props;

    const missingImagesError = getMissingImagesValidationError(profileDetails.coverImage, 'cover')
      || getMissingImagesValidationError(profileDetails.profileImage, 'profile');

    if (missingImagesError) {
      dispatchDanger(missingImagesError);
      window.scrollTo(0, 0);
      return true;
    }

    return false;
  };

  handleSubmit = () => {
    if (this.isInvalidImages()) {
      return;
    }

    const { dispatch, onSave, onAfterSave } = this.props;
    let { profileDetails } = this.state;

    // add protocols, if needed
    profileDetails = this.addProtocolToUrls(profileDetails);
    dispatch(closeNotification());

    onSave(profileDetails)
      .then((profile) => {
        this.setState(() => ({
          isModified: false
        }));
        return profile;
      })
      .then((profile) => {
        if (onAfterSave) {
          onAfterSave(profile);
        }
      })
      .catch(() => {
        window.scrollTo(0, 0);
      });
  };

  render() {
    const { profileDetails, isModified } = this.state;
    const { brandProfile, coverImageType, organizationType, mode, children } = this.props;
    const isAgencyBrand = Boolean(brandProfile.managedBy && brandProfile.managedBy.id);

    return (
      <React.Fragment>
        {
          isModified &&
            <PromptExit type="page" />
        }
        <ProfileSummary
          coverImageType={coverImageType}
          profile={profileDetails}
          sendImage={this.sendImage}
          canEdit
        />
        <BrandProfileEditForm
          mode={mode}
          organizationType={organizationType}
          isAgencyBrand={isAgencyBrand}
          brandProfile={profileDetails}
          onChange={this.handleChange}
          onSubmit={this.handleSubmit}
        >
          {children}
        </BrandProfileEditForm>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state): StateProps {
  return {
    currentAccount: state.account.currentAccount,
    roles: state.account.roles,
    brandProfile: state.brandProfile.profile,
    currentOrganization: state.organizations.currentOrganization
  };
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatch,
  dispatchDanger: message => dispatch(danger(message)),
  dispatchUploadImage: (brandProfile, type, image) => dispatch(uploadImage(brandProfile, type, image))
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandProfileEditFormContainer);
